import { useEffect, useState } from 'react';
import { notify } from '@/lib/notify';
import { Button, Modal } from '@ai-tutor/ui';
import {
  AlertTriangle,
  Clock,
  HelpCircle,
  Loader2,
  Pause,
  Play,
  Trash2,
  Video,
} from 'lucide-react';
import { lessonApi } from '@/lib/api-client';
import type { DoubtSessionSummary } from '@/lib/api-client';
import { parseUtcDate } from '@/lib/datetime';

interface VideoDoubtsListProps {
  lessonId: string;
  /** Notify parent so it can swap the video area into replay mode. */
  onReplay: (sessionId: string) => void;
  /** Notify parent that the active replay (if any) is gone. */
  onDeleted?: (sessionId: string) => void;
  /** Currently-replaying session id, used to highlight the active card. */
  activeSessionId?: string | null;
  /** Whether the active replay session is currently playing. Drives
   *  the active card's icon (Play vs. Pause). */
  activeIsPlaying?: boolean;
  /** Toggle play/pause for the currently-replaying session. Called
   *  when the user clicks the icon on the *active* card; clicking a
   *  different card still falls back to `onReplay`. */
  onTogglePlay?: () => void;
}

function formatVideoTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function formatDuration(ms: number): string {
  if (!ms) return '0s';
  const totalSeconds = Math.round(ms / 1000);
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  if (m === 0) return `${s}s`;
  return `${m}m ${s}s`;
}

function formatRelative(iso: string): string {
  const ms = Date.now() - parseUtcDate(iso).getTime();
  if (ms < 0) return 'just now';
  const s = Math.floor(ms / 1000);
  if (s < 45) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return parseUtcDate(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

export function VideoDoubtsList({
  lessonId,
  onReplay,
  onDeleted,
  activeSessionId,
  activeIsPlaying = false,
  onTogglePlay,
}: VideoDoubtsListProps) {
  const [sessions, setSessions] = useState<DoubtSessionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  // Track which row is mid-delete so we can disable its button + show
  // a spinner without blocking the rest of the list.
  const [deletingId, setDeletingId] = useState<string | null>(null);
  // Pending session targeted by the confirmation modal. The modal is
  // open iff this is non-null.
  const [pendingDelete, setPendingDelete] =
    useState<DoubtSessionSummary | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    lessonApi
      .listDoubtSessions(lessonId)
      .then(data => {
        if (cancelled) return;
        setSessions(data.sessions ?? []);
      })
      .catch(() => {
        if (!cancelled) setSessions([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [lessonId]);

  const handleConfirmDelete = async () => {
    if (!pendingDelete || deletingId) return;
    const sessionId = pendingDelete.session_id;
    setDeletingId(sessionId);
    try {
      await lessonApi.deleteDoubtSession(lessonId, sessionId);
      setSessions(prev => prev.filter(s => s.session_id !== sessionId));
      onDeleted?.(sessionId);
      setPendingDelete(null);
    } catch (err) {
      console.error('Failed to delete doubt session', err);
      notify.error('Could not delete this doubt');
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className='py-8 text-center text-sm text-muted-foreground'>
        Loading doubts…
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <div className='flex flex-col items-center justify-center px-4 py-10 text-center'>
        <HelpCircle className='mb-2 h-8 w-8 text-gray-300' />
        <p className='text-sm text-muted-foreground'>No doubts recorded yet.</p>
        <p className='text-xs text-muted-foreground'>
          Pause the video and tap "Ask Doubt" to record one.
        </p>
      </div>
    );
  }

  const isDeleting = deletingId !== null;

  return (
    <>
      <ul className='flex flex-col gap-2 p-2'>
        {sessions.map(session => {
          const isActive = activeSessionId === session.session_id;
          return (
            <li key={session.session_id}>
              <div
                role='button'
                tabIndex={0}
                onClick={() => onReplay(session.session_id)}
                onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onReplay(session.session_id);
                  }
                }}
                // Card is intentionally background-transparent so it
                // blends into the surrounding panel - the previous
                // bright-white card on a slightly off-white panel made
                // every row feel like a separate "object" rather than
                // an item in a list. A subtle border keeps it
                // delineated; hover lifts a faint surface layer.
                className={`group cursor-pointer rounded-lg border p-3 transition-colors ${
                  isActive
                    ? 'border-gray-300 bg-gray-100/70 dark:border-gray-600'
                    : 'border-transparent hover:border-border hover:bg-accent/60 dark:hover:border-gray-700/60'
                }`}
              >
                <div className='flex items-start gap-3'>
                  {/* Play/pause control. Doubles as the active-state
                    indicator: shows Pause iff this row is the
                    currently-replaying session AND it's playing.
                    Clicking the icon (with stopPropagation) lets the
                    user toggle playback without leaving the list -
                    or starts replay if this row isn't active yet. */}
                  <button
                    type='button'
                    aria-label={
                      isActive
                        ? activeIsPlaying
                          ? 'Pause replay'
                          : 'Resume replay'
                        : 'Play this doubt'
                    }
                    onClick={e => {
                      e.stopPropagation();
                      if (isActive && onTogglePlay) onTogglePlay();
                      else onReplay(session.session_id);
                    }}
                    className={`relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-colors ${
                      isActive
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground group-hover:bg-primary group-hover:text-white dark:group-hover:bg-card dark:group-hover:text-foreground'
                    }`}
                  >
                    {isActive && activeIsPlaying ? (
                      <Pause className='h-4 w-4 fill-current' />
                    ) : (
                      <Play className='ml-0.5 h-4 w-4 fill-current' />
                    )}
                  </button>
                  <div className='min-w-0 flex-1'>
                    <p
                      className='line-clamp-2 text-sm font-medium text-foreground'
                      title={session.first_question || undefined}
                    >
                      {session.title ||
                        session.first_question ||
                        '(legacy session)'}
                    </p>
                    <div className='mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground'>
                      {/* Where in the lesson video the user paused.
                       Same pill, neutral-toned to match the rest of
                       the row. */}
                      <span
                        className='inline-flex items-center gap-1 rounded-md bg-muted px-1.5 py-0.5 text-foreground'
                        title='Asked at this point in the video'
                      >
                        <Video className='h-3 w-3' />
                        <span className='font-mono'>
                          {formatVideoTime(session.video_timestamp)}
                        </span>
                      </span>
                      <span
                        className='inline-flex items-center gap-1'
                        title='When the doubt was recorded'
                      >
                        <Clock className='h-3 w-3' />
                        {formatRelative(session.started_at)}
                      </span>
                      <span aria-hidden>·</span>
                      <span>
                        {session.turn_count}{' '}
                        {session.turn_count === 1 ? 'turn' : 'turns'}
                      </span>
                      <span aria-hidden>·</span>
                      <span title='Recording length'>
                        {formatDuration(session.duration_ms)}
                      </span>
                    </div>
                  </div>
                  {/* Delete button - reveals on hover so the resting
                   row stays clean; always visible while a delete is
                   in flight. */}
                  <button
                    type='button'
                    aria-label='Delete this doubt session'
                    title='Delete'
                    disabled={deletingId === session.session_id}
                    onClick={e => {
                      e.stopPropagation();
                      setPendingDelete(session);
                    }}
                    className={`shrink-0 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-50 dark:hover:bg-red-950/40 dark:hover:text-red-400 ${
                      deletingId === session.session_id
                        ? 'opacity-100'
                        : 'opacity-0 focus:opacity-100 group-hover:opacity-100'
                    }`}
                  >
                    {deletingId === session.session_id ? (
                      <Loader2 className='h-4 w-4 animate-spin' />
                    ) : (
                      <Trash2 className='h-4 w-4' />
                    )}
                  </button>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
      <Modal
        isOpen={pendingDelete !== null}
        onClose={() => {
          if (!isDeleting) setPendingDelete(null);
        }}
        title='Delete doubt session?'
        size='sm'
        closeOnBackdropClick={!isDeleting}
        closeOnEscape={!isDeleting}
      >
        <div className='space-y-4 p-5'>
          <div className='flex items-start gap-3'>
            <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-50 text-red-600 dark:bg-red-950/40 dark:text-red-400'>
              <AlertTriangle className='h-5 w-5' />
            </div>
            <div className='min-w-0'>
              <p className='text-sm text-foreground'>
                This will permanently remove the recording, scenes, and the
                conversation transcript for this doubt. This can't be undone.
              </p>
              {pendingDelete && (
                <p className='mt-2 line-clamp-2 text-sm font-medium text-foreground'>
                  "
                  {pendingDelete.title ||
                    pendingDelete.first_question ||
                    '(legacy session)'}
                  "
                </p>
              )}
            </div>
          </div>
          <div className='flex justify-end gap-2'>
            <Button
              variant='outline'
              onClick={() => setPendingDelete(null)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant='destructive'
              onClick={handleConfirmDelete}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className='mr-1.5 h-4 w-4 animate-spin' />
                  Deleting…
                </>
              ) : (
                'Delete'
              )}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
